/**
 * Site Context — deterministic cross-checker (Site_Context SC4).
 *
 * Pure function: takes the parsed claim list + the current SED and
 * returns a list of typed ClaimConflict entries. No LLM in the loop —
 * the comparators in `comparators.ts` are simple JS predicates so the
 * result is deterministic, fast, and unit-testable.
 *
 * Engineering rules (per CLAUDE.md):
 *   - Never throws on missing SED fields. An absent provider data point
 *     reads as "provider silent" and the claim passes through into the
 *     LLM prompt without a conflict.
 *   - Comparators only fire on confident contradictions — see the
 *     conservative-bias comments in `comparators.ts`.
 *   - The resolver below is allow-listed: only the dot-paths the
 *     extractor (SC3) is allowed to emit map to real SED fields.
 *
 * Verdict-reconciliation note: surfacing site-context conflicts is the
 * one place outside the deterministic scorer where user-declared facts
 * collide with provider data. The reconciliation policy is:
 *   - Conflicts feed the LLM prompt as warnings (buildSiteContextBlock).
 *   - Conflicts surface in the wizard before submit so the user can
 *     edit / acknowledge / override.
 *   - We do NOT mutate the SED or the scoring inputs — the SC pack is
 *     additive, not corrective.
 */
import type { ClaimConflict, ExtractedClaim, SedFieldPath } from '../../types/siteContext';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { CLAIM_COMPARATORS } from './comparators';
import { isAllowedSedField } from './extractClaims';

/**
 * Resolve a claim's dot-path against the SED's data. The SED is keyed
 * by section (climate, grid, environmental, etc.) and each section's
 * payload lives under `.data`. Resolver returns `null` when the path
 * is invalid, when the section status is 'unavailable', or when the
 * leaf value is missing.
 *
 * Allow-listed: any path outside the SC3 allow-list returns null even
 * if the SED happens to carry the field — guard against extractor
 * hallucinations leaking into the cross-check.
 */
export function resolveSedField(
  sed: SiteEvidenceDocument | null,
  path: SedFieldPath,
): unknown {
  if (!sed) return null;
  if (!isAllowedSedField(path)) return null;
  const segments = path.split('.');
  if (segments.length < 2) return null;
  const [sectionKey, ...rest] = segments;
  const section = (sed as unknown as Record<string, unknown>)[sectionKey] as
    | { status?: string; data?: Record<string, unknown> }
    | undefined;
  if (!section || section.status === 'unavailable' || !section.data) return null;
  let cur: unknown = section.data;
  for (const seg of rest) {
    if (cur === null || cur === undefined || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur ?? null;
}

/**
 * Cross-check every claim against the SED. Returns the typed conflict
 * list — `[]` when there are no contradictions, when no claims have a
 * sedField, or when the SED is null.
 */
export function crossCheckClaims(
  claims: ExtractedClaim[],
  sed: SiteEvidenceDocument | null,
): ClaimConflict[] {
  const conflicts: ClaimConflict[] = [];
  for (const claim of claims) {
    // SCE-3 — mis-extracted claims do not participate in conflict
    // detection. They stay in `claims[]` for audit but every read-side
    // surface (diff, comparator, prompt block) filters them out.
    if (claim.state === 'mis-extracted') continue;
    if (!claim.sedField) continue; // claim isn't verifiable against SED
    if (!sed) continue;
    const sedValue = resolveSedField(sed, claim.sedField);
    if (sedValue === null || sedValue === undefined) continue; // provider silent
    const comparator = CLAIM_COMPARATORS[claim.category];
    if (!comparator) continue;
    try {
      const result = comparator(claim, sedValue);
      if (result) conflicts.push(result);
    } catch (e) {
      // Comparator threw — treat as "no conflict" rather than blocking
      // the wizard. Telemetry breadcrumb so we can fix it; the user
      // never sees the failure.
      console.warn(`[site-context] comparator ${claim.category} threw:`, (e as Error)?.message);
    }
  }
  return conflicts;
}
